/**
 * OrgReaderPane — reading a team's or a department's document without leaving
 * the page.
 *
 * Opening one used to dispatch `showModal({ type: 'attachmentViewer' })`, a
 * centred modal over the whole page that covered the Documents list you opened
 * it from: you could read the file or see the list, never both. As a pane it
 * docks, resizes, is remembered per user, and sits beside the list.
 *
 * This is the contacts precedent (`Clients/surface/ContactReaderPane`) applied
 * to the org units — same shape, same `ReaderComponent`, and the blob is still
 * fetched by `useGlobalStreaming` in `DocumentsTab`, so nothing about the
 * download path changed, only where the result is shown.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import ReaderComponent from '../../shared/TailwindComponents/ReaderComponent';
import Spinner from '../../shared/TailwindComponents/Spinner';
import type { OrgReaderContent } from './OrgSurfaceContext';

interface Props {
  content: OrgReaderContent | null;
  onClose: () => void;
}

const OrgReaderPane: React.FC<Props> = ({ content, onClose }) => {
  const { t } = useTranslation();

  if (!content) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <p className="text-xs text-fg-muted text-center">
          {t('Open a document to read it here.')}
        </p>
      </div>
    );
  }

  if (content.loading || !content.citation) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-6">
        <Spinner size="sm" />
        <p className="text-xs text-fg-muted text-center truncate max-w-full">{content.title}</p>
      </div>
    );
  }

  return (
    <ReaderComponent
      citation={content.citation}
      matterId={content.resourceId}
      sourceType="file"
      onClose={onClose}
    />
  );
};

export default OrgReaderPane;
